import React from "react";
import { styled } from "@mui/material/styles";
import { useTheme } from "@emotion/react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { SlideProps } from "@mui/material";
import { ChevronLeft, Close } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faXmark } from "@fortawesome/free-solid-svg-icons";

import HeaderBar from "../HeaderBar";
import IconButton from "../IconButton";
import CssBaseline from "../CssBaseline";
import Toolbar from "../Toolbar";
import Stack from "../Stack";
import Box from "../Box";
import AppBar from "../AppBar";
import Drawer from "../Drawer";
import List from "../List";
import Tooltip from "../Tooltip";
import Slide from "../Slide";
import Popover from "../Popover";

type Props = {
  children?: React.ReactNode;
  toolbarIcon?: Object;
  toolbarLeftContent?: React.ReactNode;
  toolbarRightContent?: Array<React.ReactNode>;
  levels?: Object | any;
  renderMenu?: Function;
};

const drawerWidth = 248;

const openedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(6)} + 1px)`,
});

const AppBarXl = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open?: boolean }>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerXl = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open?: boolean }>(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",

  ...((open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
    "& .MuiDrawer-paperAnchorLeft": {
      width: "248px",
    },
  }) as any),
  ...((!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
    "& .MuiDrawer-paperAnchorLeft": {
      width: "48px",
    },
  }) as any),
}));

const Transition = React.forwardRef(
  (props: SlideProps, ref: React.Ref<HTMLDivElement>) => {
    return (
      <Box ref={ref}>
        <Slide direction="right" {...props} />
      </Box>
    );
  }
);

export const UIShellNav = styled(
  (props: Props) => {
    const theme: any = useTheme();
    const isLarge = useMediaQuery(theme.breakpoints.up("lg"));
    const [open, setOpen] = React.useState(false);
    const [openXLDrawer, setXLDrawer] = React.useState(true);

    const transformRec = (ob, parent = undefined) => {
      ob.parent = parent;

      if (ob?.children) {
        for (let i = 0; i < ob.children.length; i++) {
          transformRec(ob.children[i], ob);
        }
      }
      return ob;
    };

    if (props.levels) {
      transformRec(props.levels);
    }

    const [sublevel, setSublevel] = React.useState(null);
    const [anchorEl, setAnchorEl] = React.useState(null);

    const handlePopoverClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    const openPopover = Boolean(anchorEl);

    const setNextLevel = (child) => {
      setSublevel(child);
    };

    const showUpperLevel = (child) => {
      setSublevel(child.parent?.parent ? child.parent : undefined);
    };

    const handleDrawer = () => {
      setOpen(!open);
    };

    const handleXLDrawer = () => {
      setXLDrawer(!openXLDrawer);
    };

    return (
      <Box>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <AppBarXl
            position="absolute"
            open={openXLDrawer}
            sx={{
              display: { xs: "none", xl: "block" },
            }}
          >
            <Toolbar disableGutters variant="navigation">
              <Box sx={{ flexGrow: 1 }}>
                <Stack direction="row" alignItems="center">
                  {!openXLDrawer && props.toolbarIcon && (
                    <IconButton
                      edge="start"
                      size="large"
                      onClick={handleXLDrawer}
                      sx={{ width: 48, color: "white", mr: 2 }}
                    >
                      {props.toolbarIcon}
                    </IconButton>
                  )}
                  {props.toolbarLeftContent}
                </Stack>
              </Box>

              <Box sx={{ flexGrow: 0 }}>
                <Stack direction="row" alignItems="center">
                  {props.toolbarRightContent &&
                    props.toolbarRightContent.map((item, index) => (
                      <Box key={index}>{item}</Box>
                    ))}
                </Stack>
              </Box>
            </Toolbar>
          </AppBarXl>
          <DrawerXl
            variant="permanent"
            open={openXLDrawer}
            sx={{ display: { xs: "none", xl: "block" } }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                padding: theme.spacing(0, 1),
                height: 48,
              }}
            >
              <IconButton onClick={handleXLDrawer}>
                <ChevronLeft />
              </IconButton>
            </Box>
            <Box>
              {props.renderMenu ? (
                props.renderMenu({ isMenuOpen: openXLDrawer })
              ) : (
                <List disablePadding>
                  {props.levels.children?.map((child, childIndex) => {
                    return (
                      <React.Fragment key={childIndex}>
                        {child?.renderMenuItem &&
                          child?.renderMenuItem({
                            isMenuOpen: openXLDrawer,
                            openSubMenu: (event) => {
                              setNextLevel(child);
                              handlePopoverClick(event);
                            },
                          })}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          </DrawerXl>
          <AppBar
            position="absolute"
            open={open}
            sx={{
              display: { xs: "block", xl: "none" },
            }}
          >
            <Toolbar disableGutters variant="navigation" size="small">
              <Box sx={{ flexGrow: 1 }}>
                <Stack direction="row" alignItems="center">
                  {!isLarge && props.toolbarIcon && (
                    <IconButton
                      edge="start"
                      onClick={handleDrawer}
                      size="large"
                      sx={{ width: 48, color: "white", mr: 2 }}
                    >
                      {open ? (
                        <Close sx={{ color: "white" }} />
                      ) : (
                        props.toolbarIcon
                      )}
                    </IconButton>
                  )}
                  {props.toolbarLeftContent}
                </Stack>
              </Box>

              <Box sx={{ flexGrow: 0 }}>
                <Stack direction="row" alignItems="center">
                  {props.toolbarRightContent &&
                    props.toolbarRightContent.map((item, index) => (
                      <Box key={index}>{item}</Box>
                    ))}
                </Stack>
              </Box>
            </Toolbar>
          </AppBar>
          <Drawer
            anchor="left"
            variant={isLarge ? "permanent" : "temporary"}
            ModalProps={{ onBackdropClick: handleDrawer }}
            sx={{
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
              },
              display: { xs: "block", xl: "none" },
              "& .MuiDrawer-paperAnchorLeft": {
                marginTop: "48px",
              },
            }}
            open={open}
          >
            <Box>
              {props.renderMenu ? (
                props.renderMenu({ isMenuOpen: open })
              ) : (
                <List disablePadding>
                  {isLarge
                    ? props.levels.children?.map((child, index) => (
                        <Tooltip
                          arrow
                          key={index}
                          title={child.name}
                          placement="right"
                        >
                          {child?.renderMenuItem &&
                            child?.renderMenuItem({
                              isMenuOpen: open,
                              openSubMenu: (event) => {
                                setNextLevel(child);
                                handlePopoverClick(event);
                              },
                            })}
                        </Tooltip>
                      ))
                    : props.levels.children?.map((child, index) => (
                        <React.Fragment key={index}>
                          {child?.renderMenuItem &&
                            child?.renderMenuItem({
                              isMenuOpen: open,
                              openSubMenu: (event) => {
                                setNextLevel(child);
                                handlePopoverClick(event);
                              },
                            })}
                        </React.Fragment>
                      ))}
                </List>
              )}
            </Box>
          </Drawer>
          {sublevel && (
            <Popover
              open={openPopover}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              PaperProps={{
                variant: "menuItem",
              }}
              BackdropProps={{
                invisible: true,
              }}
              TransitionComponent={Transition}
            >
              <div>
                <HeaderBar
                  reduced
                  size="small"
                  title={sublevel?.name}
                  divider
                  leftItems={[
                    sublevel?.parent.parent !== undefined && (
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => showUpperLevel(sublevel)}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} />
                      </IconButton>
                    ),
                  ]}
                  rightItems={[
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handlePopoverClose()}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </IconButton>,
                  ]}
                />
                <List disablePadding>
                  {sublevel.children.map(
                    (child: any, childIndex) => (
                      // child?.display ||
                      // (child.display === undefined && (
                      <React.Fragment key={childIndex}>
                        {child?.renderMenuItem &&
                          child?.renderMenuItem({
                            openSubMenu: (event) => {
                              if (child.children) {
                                setNextLevel(child);
                              }
                            },
                          })}
                      </React.Fragment>
                    )
                    // <ListItemButton
                    //   key={childIndex}
                    //   onClick={() => {
                    //     if (child.children) {
                    //       setNextLevel(child);
                    //     } else {
                    //       child?.onClick();
                    //     }
                    //   }}
                    //   disabled={child.disabled ? true : false}
                    // >
                    //   <ListItemText
                    //     primary={child.name}
                    //     sx={{
                    //       "&.MuiListItemText-root .MuiTypography-root": {
                    //         overflow: "hidden",
                    //         whiteSpace: "nowrap",
                    //         textOverflow: "ellipsis",
                    //       },
                    //     }}
                    //   />
                    //   {child.children && <ChevronRight />}
                    // </ListItemButton>
                    // ))
                  )}
                </List>
              </div>
            </Popover>
          )}
          <Box sx={{ flexGrow: 1, mt: "48px" }}>{props.children}</Box>
        </Box>
      </Box>
    );
  },
  {
    name: "UIShellNav",
    slot: "Root",
    overridesResolver: (props, styles) => [],
  }
)(({ theme }) => ({}));
